import React from 'react';
import { useStaticQuery, graphql, navigate } from 'gatsby';
import styled, { css } from 'styled-components';
import {
  breakpointSizes,
  minBreakpointQuery,
  maxBreakpointQuery,
  standardColours,
  brandColours,
  siteThemeColours,
  brandFonts,
  fluidFontSize,
  fontSize,
  standardTransition,
  visuallyHidden,
} from '../styles';
import { Image, Svg, Link } from './ui';
import mothersDayBadge from '../images/site-themes/mothers-day/mothers-day-badge.svg';

// moved up for hover
const StyledContent = styled.div`
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  overflow: hidden;
  padding: 30px 25px;
  color: ${standardColours.white};

  ${minBreakpointQuery.small`
    padding: 45px 30px;
  `}

  ${minBreakpointQuery.large`
    padding 60px 35px;
  `}

  &:before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background: linear-gradient(
      to bottom,
      transparent 0%,
      ${({ siteTheme, alt }) =>
          siteTheme
            ? standardColours.black
            : alt
              ? brandColours.tertiary
              : brandColours.primary}
        100%
    );
    opacity: 0.8;
    transform: translateY(100%);
    transition: ${standardTransition('transform')};
  }
`;

const StyledImageCard = styled.article`
  position: relative;
  cursor: pointer;

  &:hover {
    ${StyledContent} {
      &:before {
        transform: translateY(0);
      }
    }
  }
`;

const StyledStrip = styled.p`
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  z-index: 1;
  padding: 5px;
  color: ${({ siteTheme }) =>
    siteTheme === 'valentines'
      ? siteThemeColours.valentines.secondary
      : siteTheme === 'blackfriday'
        ? brandColours.tertiary
        : standardColours.white};
  letter-spacing: 1px;
  text-transform: uppercase;
  text-align: center;
  background-color: ${({ siteTheme }) =>
    siteTheme === 'valentines'
      ? siteThemeColours.valentines.tertiary
      : siteTheme === 'mothersday'
        ? siteThemeColours.mothersDay.primary
        : siteTheme === 'summer'
          ? siteThemeColours.summer.primary
          : siteTheme === 'christmas'
            ? siteThemeColours.christmas.secondary
            : siteTheme === 'blackfriday'
              ? standardColours.black
              : brandColours.primary};

  ${({ siteTheme }) => {
    if (siteTheme === 'blackfriday') {
      return css`
        text-shadow: 0 0 4px ${brandColours.tertiary};
      `;
    }
  }}
`;

const StyledImageWrapper = styled.div`
  position: relative;
`;

const StyledImage = styled(Image)`
  position: relative;
  display: block;

  &:after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background: linear-gradient(
      to bottom,
      transparent 0%,
      ${standardColours.black} 100%
    );
    opacity: 0.8;
  }
`;

const StyledBadge = styled(Svg)`
  ${maxBreakpointQuery.mlarge`
    ${visuallyHidden()};
  `}

  ${minBreakpointQuery.mlarge`
    position: absolute;
    top: 45px;
    right: 15px;
    height: 70px;
    width: 70px;
  `}

  ${minBreakpointQuery.large`
    top: 50px;
    right: 20px;
    height: 80px;
    width: 80px;
  `}
`;

const StyledHeading = styled.h3`
  position: relative;
  ${brandFonts.secondary};
  ${fluidFontSize(
    '24px',
    '36px',
    breakpointSizes.tiny,
    breakpointSizes.xxlarge,
  )};
  line-height: 1.2;
`;

const StyledText = styled.p`
  position: relative;
  margin-top: 6px;
  line-height: 1.5;

  ${minBreakpointQuery.large`
    margin-top: 10px;
    ${fontSize(18)};
  `}
`;

const ImageCard = ({ label, heading, image, text, url, alt, ...props }) => {
  const {
    spaseekers: {
      promotion: { theme: siteTheme, offers },
    },
  } = useStaticQuery(graphql`
    query ImageCardQuery {
      spaseekers {
        promotion {
          theme
          offers {
            bannerText
          }
        }
      }
    }
  `);

  let bannerText = !!offers ? offers.bannerText : '';

  return (
    <StyledImageCard
      {...props}
      onClick={() => {
        navigate(url);
      }}
    >
      {siteTheme && (
        <StyledStrip siteTheme={siteTheme}>
          {label ? label : bannerText}
        </StyledStrip>
      )}
      <StyledImageWrapper>
        <StyledImage
          src={image && image.url}
          alt={image && image.title}
          width={730}
          height={950}
          layout="constrained"
        />
      </StyledImageWrapper>
      <StyledContent siteTheme={siteTheme} alt={alt}>
        <StyledHeading>
          <Link to={url}>{heading || text}</Link>
        </StyledHeading>
        {heading && text && <StyledText>{text}</StyledText>}
      </StyledContent>
    </StyledImageCard>
  );
};

export default ImageCard;
